import { useQueryDictationPostProcessingVariant } from '@/features/dictation/dictationBubble/csat/hooks/queries/useQueryDictationPostProcessingVariant';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import {
  dictationCsatDismissAtom,
  dictationCsatPopoverStateAtom,
  getDictationAnalyticsPorts,
} from '@heidi/dictate';
import { isTauri } from '@heidi/runtime/platform';
import { CSATRating } from '@heidi/schema/ml-scribe';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export const dictationCsatSchema = z.object({
  stars: z.nativeEnum(CSATRating),
  comment: z.string().optional(),
});

export type DictationCsatFormValues = z.infer<typeof dictationCsatSchema>;

export function useDictationCsatForm() {
  const dismiss = useSetAtom(dictationCsatDismissAtom);
  const { isOpen } = useAtomValue(dictationCsatPopoverStateAtom);
  const platform = isTauri() ? 'desktop' : 'web';

  const { data: outputLanguages } = useQueryUser((u) => u?.dictation?.output_languages);
  const { data: variantData } = useQueryDictationPostProcessingVariant({
    isDesktop: platform === 'desktop',
    outputLanguages,
    enabled: isOpen,
  });

  const form = useForm<DictationCsatFormValues>({
    resolver: zodResolver(dictationCsatSchema),
    defaultValues: {
      comment: '',
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    const comment = values.comment?.trim();
    getDictationAnalyticsPorts().csat.trackSubmitted({
      stars: values.stars,
      comment: comment || undefined,
      post_processing_model: variantData?.variant ?? null,
      platform,
    });
    dismiss();
  });

  // Stable identity: consumed as a useEffect dep for the auto-dismiss timer.
  const handleDismiss = useCallback(() => {
    if (!isOpen) return;
    getDictationAnalyticsPorts().csat.trackDismissed({
      platform,
    });
    dismiss();
  }, [isOpen, platform, dismiss]);

  return { form, handleSubmit, handleDismiss };
}
